/**
 * useJobs — React hook for the local player's job-advancement state.
 *
 * Exposes current job snapshot, class-correct advancement paths, and
 * the authoritative `advanceJob()` action via `job:advance`.
 *
 * Source of truth is `useGame()` (job/class/level) + `JOB_ADVANCEMENTS`.
 * GameProvider retains authoritative job updates even while this window is closed.
 */

import { useCallback, useMemo } from 'react';
import { useGame } from '../contexts/GameContext';
import {
  CharacterClass,
  JobClass,
  type JobAdvancement,
} from '../../../shared/CharacterTypes';
import { JOB_ADVANCEMENTS } from '../../../shared/classes/JobAdvancements';
import { ServiceRegistry } from '../../core/ServiceRegistry';

export interface JobPath {
  id: JobClass;
  name: string;
  description: string;
  requiredLevel: number;
  /** True when the player meets the level gate for this advancement. */
  available: boolean;
}

export interface JobsState {
  /** The player's current job (null until a character is loaded). */
  currentJob: JobClass | null;
  /** The player's class. */
  className: CharacterClass | null;
  /** The player's level. */
  level: number;
  /** Class-correct advancement paths, sorted by required level. */
  paths: JobPath[];
}

export interface JobsActions {
  /** Dispatches authoritative job advancement request to the server. */
  advanceJob: (targetJob: JobClass) => void;
}

export type JobsResult = JobsState & JobsActions;

/**
 * Build the advancement paths for a class at a given level from the canonical
 * `JOB_ADVANCEMENTS` table. Exported for unit testing (pure, no React).
 *
 * Excludes the level-1 *_basic entry (the starting job, not an "advancement").
 */
export function buildJobPaths(className: CharacterClass, level: number): JobPath[] {
  return Object.values(JOB_ADVANCEMENTS)
    .filter((adv: JobAdvancement) => adv.requiredClass === className && adv.requiredLevel > 1)
    .sort((a, b) => a.requiredLevel - b.requiredLevel)
    .map((adv) => ({
      id: adv.job,
      name: adv.name,
      description: adv.description,
      requiredLevel: adv.requiredLevel,
      available: level >= adv.requiredLevel,
    }));
}

export function useJobs(): JobsResult {
  const { player } = useGame();

  const className = player?.class ?? null;
  const level = player?.level ?? 1;

  const paths = useMemo(
    () => (className ? buildJobPaths(className, level) : []),
    [className, level],
  );

  const advanceJob = useCallback((targetJob: JobClass) => {
    try {
      const room = ServiceRegistry.getInstance().get('NetworkManager', { optional: true })?.getRoom?.() ?? null;
      room?.send('job:advance', { targetJob });
    } catch {
      // Non-critical
    }
  }, []);

  return {
    currentJob: player?.job ?? null,
    className,
    level,
    paths,
    advanceJob,
  };
}
